import React, { useEffect, useState } from "react";
import { Crown, Sparkles, X, Percent } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { pickTier, tierStyle } from "@/lib/loyalty";

export const TierCelebration = () => {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [newTier, setNewTier] = useState(null);

  useEffect(() => {
    api.get("/loyalty-config").then(({ data }) => setTiers(data.tiers || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user || user.role === "admin" || tiers.length === 0) return;
    const lifetime = user.points_lifetime ?? user.points ?? 0;
    const current = pickTier(tiers, lifetime);
    if (!current) return;
    const key = `m16_tier_${user.id}`;
    const prev = localStorage.getItem(key);
    const idxOf = (k) => tiers.findIndex((tr) => tr.key === k);
    if (prev !== null && idxOf(current.key) > idxOf(prev)) {
      setNewTier(current);
    }
    localStorage.setItem(key, current.key);
  }, [user, tiers]);

  if (!newTier) return null;
  const st = tierStyle(newTier.key);
  const name = lang === "ar" ? newTier.name_ar : newTier.name_en;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md" data-testid="tier-celebration">
      <div
        className="relative w-full max-w-md rounded-3xl border bg-gradient-to-br from-[#121214] to-[#0A0A0B] p-8 text-center"
        style={{ borderColor: st.border }}
      >
        <button
          onClick={() => setNewTier(null)}
          data-testid="tier-celebration-close"
          className="absolute top-4 ltr:right-4 rtl:left-4 text-[#9CA3AF] hover:text-[#F3F4F6]"
        >
          <X size={20} />
        </button>

        <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-40 h-40 rounded-full blur-3xl" style={{ backgroundColor: st.bg }} />

        <div className="relative">
          <div
            className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-5 animate-pulse"
            style={{ backgroundColor: st.bg, border: `2px solid ${st.border}`, color: st.color }}
          >
            <Crown size={38} />
          </div>

          <div className="flex items-center justify-center gap-2 text-xs uppercase tracking-widest mb-2" style={{ color: st.color }}>
            <Sparkles size={14} /> {t("tierUpTitle")} <Sparkles size={14} />
          </div>
          <h2 className="font-display text-4xl mb-2" style={{ color: st.color }}>{name}</h2>
          <p className="text-sm text-[#9CA3AF] mb-6">{t("tierUpDesc")}</p>

          <div className="space-y-2 text-sm bg-[#0A0A0B]/50 border border-[#27272A] rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-[#9CA3AF] inline-flex items-center gap-1"><Sparkles size={13} /> {t("tierMultiplier")}</span>
              <span className="num-ltr" style={{ color: st.color }}>×{newTier.multiplier}</span>
            </div>
            {newTier.discount_percent > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-[#9CA3AF] inline-flex items-center gap-1"><Percent size={13} /> {t("tierExclusiveDiscount")}</span>
                <span className="num-ltr" style={{ color: st.color }}>{newTier.discount_percent}%</span>
              </div>
            )}
          </div>

          <button
            onClick={() => setNewTier(null)}
            data-testid="tier-celebration-ok"
            className="btn-gold px-8 py-3 rounded-full w-full"
          >
            {t("tierUpCta")}
          </button>
        </div>
      </div>
    </div>
  );
};
